import React from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart3, TrendingUp, Clock, Calendar, Award, 
  Activity, Users, Target, Zap, Coffee 
} from 'lucide-react';
import { useWorkStore } from '../../store/work-buttons/workStore';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer 
} from 'recharts';

export function Analytics() {
  const { activities, getActivityStats } = useWorkStore();
  const stats = getActivityStats();

  // Prepare data for charts
  const categoryData = Object.entries(stats.byCategory).map(([name, value]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    value
  }));

  const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

  // Daily activity trend (mock data for demo)
  const dailyTrend = [
    { day: 'Mon', clicks: 12 },
    { day: 'Tue', clicks: 15 },
    { day: 'Wed', clicks: 18 },
    { day: 'Thu', clicks: 14 },
    { day: 'Fri', clicks: 20 },
    { day: 'Sat', clicks: 5 },
    { day: 'Sun', clicks: 3 },
  ];

  // Recent activities
  const recentActivities = activities.slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={<Zap className="text-yellow-500" />}
          label="Total Clicks"
          value={stats.total}
          change="+12%"
          positive={true}
        />
        <StatCard
          icon={<Calendar className="text-blue-500" />}
          label="Today"
          value={stats.today}
          change={`${stats.today > 10 ? 'Productive!' : 'Keep going!'}`}
          positive={true}
        />
        <StatCard
          icon={<TrendingUp className="text-green-500" />}
          label="This Week"
          value={stats.thisWeek}
          change="+8%"
          positive={true}
        />
        <StatCard
          icon={<Award className="text-purple-500" />}
          label="Streak"
          value="5 days"
          change="Personal best!"
          positive={true}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Category Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Button Usage by Category
          </h3>
          {categoryData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={categoryData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {categoryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[250px] flex items-center justify-center text-gray-400">
              No data yet. Start clicking buttons!
            </div>
          )}
        </motion.div>

        {/* Weekly Trend */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Weekly Activity
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={dailyTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="day" stroke="#6b7280" />
              <YAxis stroke="#6b7280" />
              <Tooltip />
              <Bar dataKey="clicks" fill="#3B82F6" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* Recent Activity */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Recent Activity
        </h3>
        {recentActivities.length > 0 ? (
          <div className="space-y-3">
            {recentActivities.map((activity) => (
              <div
                key={activity.id}
                className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0"
              >
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full" />
                  <span className="text-gray-900 dark:text-white font-medium">
                    {activity.label}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                    {activity.category}
                  </span>
                </div>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {new Date(activity.timestamp).toLocaleTimeString()}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-400">No recent activity</p>
        )}
      </motion.div>

      {/* Fun Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg shadow-md p-6 text-white"
      >
        <h3 className="text-lg font-semibold mb-4">Fun Facts</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <Coffee className="mx-auto mb-2" size={32} />
            <p className="text-2xl font-bold">42</p>
            <p className="text-sm opacity-90">Coffee breaks taken</p>
          </div>
          <div className="text-center">
            <Users className="mx-auto mb-2" size={32} />
            <p className="text-2xl font-bold">156</p>
            <p className="text-sm opacity-90">Team interactions</p>
          </div>
          <div className="text-center">
            <Target className="mx-auto mb-2" size={32} />
            <p className="text-2xl font-bold">98%</p>
            <p className="text-sm opacity-90">Productivity score</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  change: string;
  positive: boolean;
}

function StatCard({ icon, label, value, change, positive }: StatCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: 1.02 }}
      className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6"
    >
      <div className="flex items-center justify-between mb-4">
        {icon}
        <span className={`text-xs font-medium ${positive ? 'text-green-600' : 'text-red-600'}`}>
          {change}
        </span>
      </div>
      <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
      <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
    </motion.div>
  );
}